import React, { memo, useState, useEffect, useCallback } from 'react';
import { useDebounce } from '../hooks/useDebounce';
import { X, Search } from 'lucide-react';

const NewConversationModal = ({ 
  isOpen, 
  onClose, 
  onStartConversation, 
  token,
  darkMode,
  instancias = [],
}) => {
  const [telefone, setTelefone] = useState('');
  const [instanciaId, setInstanciaId] = useState('');
  const [contatos, setContatos] = useState([]);
  const [contatosFiltrados, setContatosFiltrados] = useState([]);
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false);
  const debouncedTelefone = useDebounce(telefone, 300);

  // Só instâncias WhatsApp (não dá pra iniciar conversa em IG/FB)
  const whatsappInstancias = instancias.filter(i => i.platform === 'whatsapp');

  useEffect(() => {
    if (isOpen && whatsappInstancias.length > 0 && !instanciaId) {
      setInstanciaId(whatsappInstancias[0].id);
    }
  }, [isOpen, whatsappInstancias.length]); // eslint-disable-line

  const carregarContatos = useCallback(async () => {
    if (!token) return;
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/contatos/list/${user.cliente_id}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Erro');
      const data = await response.json();
      setContatos(data.messages || data || []);
    } catch (e) {
      console.error('Erro ao carregar contatos:', e);
      setContatos([]);
    }
  }, [token]);

  const filtrarContatos = useCallback((texto) => {
    if (!texto || texto.length < 2 || !Array.isArray(contatos)) {
      setContatosFiltrados([]);
      setMostrarSugestoes(false);
      return;
    }
    const filtrados = contatos.filter(c => 
      c.nome?.toLowerCase().includes(texto.toLowerCase()) || c.telefone?.includes(texto)
    ).slice(0, 8);
    setContatosFiltrados(filtrados);
    setMostrarSugestoes(filtrados.length > 0);
  }, [contatos]);

  useEffect(() => {
    if (/^\d+$/.test(debouncedTelefone)) {
      // Se é só número, filtra por telefone
      filtrarContatos(debouncedTelefone);
    } else {
      filtrarContatos(debouncedTelefone);
    }
  }, [debouncedTelefone, filtrarContatos]);

  const selecionarContato = useCallback((contato) => {
    setTelefone(contato.telefone);
    setMostrarSugestoes(false);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!telefone.trim() || !instanciaId) return;
    
    const contatoSelecionado = Array.isArray(contatos) ? contatos.find(c => 
      c.nome === telefone || c.telefone === telefone
    ) : null;
    
    // Aceita: número puro, group_id (com -group ou @g.us), ou telefone do contato selecionado
    const isGroupId = telefone.includes('-group') || telefone.includes('@g.us');
    const telefoneNumero = (/^\d+$/.test(telefone) || isGroupId) ? telefone : contatoSelecionado?.telefone;
    const nome = contatoSelecionado?.nome || telefone;
    
    if (!telefoneNumero) return;
    
    onStartConversation({
      telefone: telefoneNumero,
      nome_contato: nome,
      instancia_id: instanciaId,
      ultima_mensagem: '',
      eh_resposta: false,
      respondida_por_humano: false,
      create_at: new Date().toISOString(),
      tem_pendente: false,
    });
    handleClose();
  }, [telefone, instanciaId, contatos, onStartConversation]); // eslint-disable-line

  const handleClose = useCallback(() => {
    setTelefone('');
    setMostrarSugestoes(false);
    setContatosFiltrados([]);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen && token) carregarContatos();
  }, [isOpen, token, carregarContatos]);

  if (!isOpen) return null;

  const inputCls = darkMode
    ? 'bg-zinc-700 border-zinc-600 text-white placeholder-gray-400'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className={`rounded-xl p-6 w-[440px] max-w-md mx-4 shadow-2xl ${darkMode ? 'bg-zinc-800 border border-zinc-700' : 'bg-white'}`}>
        <div className="flex items-center justify-between mb-5">
          <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Nova Conversa</h3>
          <button onClick={handleClose} className={darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'}>
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Seleção de instância */}
        <div className="mb-4">
          <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Enviar por qual número?
          </label>
          {whatsappInstancias.length === 0 ? (
            <p className={`text-sm ${darkMode ? 'text-red-400' : 'text-red-500'}`}>Nenhuma instância WhatsApp conectada</p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {whatsappInstancias.map(inst => (
                <button key={inst.id} onClick={() => setInstanciaId(inst.id)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-all border ${
                    instanciaId === inst.id
                      ? 'bg-green-600 text-white border-green-600'
                      : darkMode ? 'bg-zinc-700 text-gray-300 border-zinc-600 hover:border-zinc-500' : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300'
                  }`}>
                  <span>📱</span>
                  <span className="font-medium">{inst.nome_instancia}</span>
                  {inst.numero_whatsapp && <span className={`text-xs ml-auto ${instanciaId === inst.id ? 'text-green-200' : darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{inst.numero_whatsapp}</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Busca de contato */}
        <div className="mb-4 relative">
          <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Contato
          </label>
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
            <input type="text" value={telefone}
              onChange={e => setTelefone(e.target.value)}
              onFocus={() => { if (telefone) filtrarContatos(telefone); }}
              placeholder="Nome do contato ou telefone (5519999999999)"
              className={`w-full pl-10 pr-3 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-green-500 ${inputCls}`} />
          </div>
          
          {mostrarSugestoes && (
            <div className={`absolute top-full left-0 right-0 mt-1 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto ${
              darkMode ? 'bg-zinc-700 border border-zinc-600' : 'bg-white border border-gray-200'
            }`}>
              {contatosFiltrados.map((contato, i) => (
                <div key={i} onClick={() => selecionarContato(contato)}
                  className={`px-3 py-2.5 cursor-pointer border-b last:border-b-0 ${
                    darkMode ? 'border-zinc-600 hover:bg-zinc-600' : 'border-gray-100 hover:bg-gray-50'
                  }`}>
                  <div className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{contato.nome}</div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{contato.telefone}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={handleClose}
            className={`px-4 py-2 rounded-lg text-sm ${darkMode ? 'text-gray-300 hover:bg-zinc-700' : 'text-gray-600 hover:bg-gray-100'}`}>
            Cancelar
          </button>
          <button onClick={handleSubmit}
            disabled={!telefone.trim() || !instanciaId}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
              !telefone.trim() || !instanciaId
                ? darkMode ? 'bg-zinc-600 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}>
            Iniciar Conversa
          </button>
        </div>
      </div>
    </div>
  );
};

export default memo(NewConversationModal);
